import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import LotteryWeekSelector, {
  getInitialWeekStart,
  getWeekRange,
} from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import apiClient from '../../../utils/axiosClient';
import { exportToExcel } from '../../../utils/excelExport';
import type {
  LuckyLotteryRevenueExcelResponse,
  LuckyLotteryRevenueResponse,
} from '../../../types/luckyLottery.types';

dayjs.locale('ko');

const STORAGE_KEY = 'avatye.ops.lucky-lottery.revenue.filter';

type StoredFilter = {
  startDate: string;
  endDate: string;
  appName: string;
};

const getStoredFilter = (): StoredFilter | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilter;
  } catch {
    // ignore
  }
  return null;
};

const saveFilter = (filter: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
};

const KRW = (v: number) => `₩${v.toLocaleString('ko-KR')}`;

export default function LuckyLotteryRevenue() {
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate) {
      return { startDate: stored.startDate, endDate: stored.endDate };
    }
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState<string>(initialWeek.startDate);
  const [endDate, setEndDate] = useState<string>(initialWeek.endDate);
  const [selectedApp, setSelectedApp] = useState<string | null>(stored?.appName ?? '앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [commission, setCommission] = useState(false);
  // 비어 있으면 DB에 저장된 환율을 쓴다. Enter·포커스 이탈 때만 반영해 입력 중 재조회를 막는다.
  const [rateInput, setRateInput] = useState('');
  const [usdKrwRate, setUsdKrwRate] = useState<number | null>(null);
  const [data, setData] = useState<LuckyLotteryRevenueResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = selectedApp === '앱 전체' ? null : selectedApp;
    saveFilter({ startDate, endDate, appName: selectedApp ?? '앱 전체' });
    setLoading(true);
    apiClient
      .get<LuckyLotteryRevenueResponse>('/api/v1/stat/lucky-lottery/summary', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
          ...(commission ? { commission: true } : {}),
          ...(usdKrwRate ? { usd_krw_rate: usdKrwRate } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs, commission, usdKrwRate]);

  const handleDownloadExcel = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = selectedApp === '앱 전체' ? null : selectedApp;
    apiClient
      .get<LuckyLotteryRevenueExcelResponse>('/api/v1/stat/lucky-lottery/summary/excel', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
          ...(commission ? { commission: true } : {}),
          ...(usdKrwRate ? { usd_krw_rate: usdKrwRate } : {}),
        },
      })
      .then((res) => {
        const rows = res.data.daily.map((r) => ({
          date: r.date,
          appID: r.appID,
          appDescription: r.appDescription,
          adpopcornRevenue: r.adpopcornRevenue,
          buzzvilRevenue: r.buzzvilRevenue,
          rcRevenue: r.rcRevenue,
          naverDirectRevenue: r.naverDirectRevenue,
          coupangSellerRevenue: r.coupangSellerRevenue,
          total:
            r.adpopcornRevenue +
            r.buzzvilRevenue +
            r.rcRevenue +
            r.naverDirectRevenue +
            r.coupangSellerRevenue,
        }));
        exportToExcel(
          rows,
          `럭키복권_매출_${dayjs(startDate).format('YYMMDD')}_${dayjs(endDate).format('YYMMDD')}.xlsx`,
          '매출',
          [
            '날짜',
            '앱ID',
            '앱명',
            '애드팝콘',
            '버즈빌',
            'RC',
            '네이버 직연동',
            '쿠팡 셀러',
            '합계',
          ],
        );
      })
      .catch(() => setErrorMessage('엑셀 다운로드에 실패했습니다.'));
  }, [startDate, endDate, selectedApp, selectedOs, commission, usdKrwRate]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyRate = () => {
    const trimmed = rateInput.trim();
    const rate = Number(trimmed);
    if (trimmed === '') {
      setUsdKrwRate(null);
    } else if (Number.isInteger(rate) && rate > 0 && rate < 10000) {
      setUsdKrwRate(rate);
    } else {
      setRateInput(usdKrwRate ? String(usdKrwRate) : '');
    }
  };

  const chartData = useMemo(() => {
    if (!data?.daily.length) return [];
    return [...data.daily]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((r) => ({
        date: dayjs(r.date).format('MM/DD'),
        revenue:
          r.adpopcornRevenue +
          r.buzzvilRevenue +
          r.rcRevenue +
          r.naverDirectRevenue +
          r.coupangSellerRevenue,
      }));
  }, [data]);

  const totals = useMemo(() => {
    if (!data?.daily.length) return null;
    return data.daily.reduce(
      (acc, r) => ({
        adpopcornRevenue: acc.adpopcornRevenue + r.adpopcornRevenue,
        buzzvilRevenue: acc.buzzvilRevenue + r.buzzvilRevenue,
        rcRevenue: acc.rcRevenue + r.rcRevenue,
        naverDirectRevenue: acc.naverDirectRevenue + r.naverDirectRevenue,
        coupangSellerRevenue: acc.coupangSellerRevenue + r.coupangSellerRevenue,
      }),
      {
        adpopcornRevenue: 0,
        buzzvilRevenue: 0,
        rcRevenue: 0,
        naverDirectRevenue: 0,
        coupangSellerRevenue: 0,
      },
    );
  }, [data]);

  const sortedRows = useMemo(() => {
    if (!data?.daily.length) return [];
    return [...data.daily].sort((a, b) => b.date.localeCompare(a.date));
  }, [data]);

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      {/* 필터 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>
        <Box
          sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* 카드 */}
      <Box sx={{ ...PAGE_CARD_SX, width: '100%', maxWidth: '100%' }}>
        {/* 앱 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* 차트 */}
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1.5 }}>
              일별 매출 추이
            </Typography>
            {chartData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                <Typography color="#94a3b8">데이터가 없습니다.</Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" />
                    <YAxis
                      tickFormatter={(v: number) =>
                        v >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${v}`
                      }
                    />
                    <RechartsTooltip
                      formatter={(value: number) => `₩${value.toLocaleString('ko-KR')}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#0044D2"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                      name="매출"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

            {/* 계산 옵션: 수수료와 환율은 서로 독립 — 둘 다 켜면 환율로 원화를 만든 뒤 수수료를 뺀다 */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                p: 1.5,
                mb: 2,
                borderRadius: 1,
                backgroundColor: '#f8fafc',
                border: '1px solid #e5e7eb',
              }}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={commission}
                      onChange={(e) => setCommission(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#0044D2' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0044D2',
                        },
                      }}
                    />
                  }
                  label="수수료 적용"
                  sx={{
                    ml: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: '#475569',
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {commission ? '수수료를 뺀 순매출' : '수수료를 빼기 전 총매출'}
                </Typography>
              </Box>
              <Box>
                <TextField
                  size="small"
                  label="USD 환율 적용"
                  placeholder="예: 1350"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value.replace(/\D/g, ''))}
                  onBlur={applyRate}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyRate();
                  }}
                  slotProps={{
                    htmlInput: { inputMode: 'numeric', maxLength: 4 },
                    input: { endAdornment: <InputAdornment position="end">원</InputAdornment> },
                  }}
                  sx={{ width: 160 }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5 }}>
                  {usdKrwRate
                    ? `애드팝콘·RC를 1 USD = ${usdKrwRate.toLocaleString('ko-KR')}원으로 계산`
                    : '비우면 일자별 저장 환율 · 애드팝콘·RC에만 적용 · Enter로 반영'}
                </Typography>
              </Box>
            </Box>
            {/* 테이블 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                일별 상세 현황
              </Typography>
              <TableActionsMenu onDownloadExcel={handleDownloadExcel} />
            </Box>
            {sortedRows.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography color="#94a3b8">데이터가 없습니다.</Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 0, border: '1px solid #f1f5f9' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        '날짜',
                        '애드팝콘',
                        '버즈빌',
                        'RC',
                        '네이버 직연동',
                        '쿠팡 셀러',
                        '합계',
                      ].map((col) => (
                        <TableCell
                          key={col}
                          align={col === '날짜' ? 'left' : 'right'}
                          sx={{
                            fontWeight: 700,
                            bgcolor: '#f8fafc',
                            borderBottom: '2px solid #e2e8f0',
                            fontSize: '0.82rem',
                            color: '#475569',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {totals && (
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            color: '#1e293b',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          합계
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}
                        >
                          {KRW(totals.adpopcornRevenue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}
                        >
                          {KRW(totals.buzzvilRevenue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}
                        >
                          {KRW(totals.rcRevenue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}
                        >
                          {KRW(totals.naverDirectRevenue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}
                        >
                          {KRW(totals.coupangSellerRevenue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#0044D2' }}
                        >
                          {KRW(
                            totals.adpopcornRevenue +
                              totals.buzzvilRevenue +
                              totals.rcRevenue +
                              totals.naverDirectRevenue +
                              totals.coupangSellerRevenue,
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    {sortedRows.map((row) => {
                      const total =
                        row.adpopcornRevenue +
                        row.buzzvilRevenue +
                        row.rcRevenue +
                        row.naverDirectRevenue +
                        row.coupangSellerRevenue;
                      return (
                        <TableRow
                          key={row.date}
                          sx={{
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f0f4ff' },
                            borderTop: '1px solid #e2e8f0',
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.82rem',
                              color: '#1e293b',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.date}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                            {KRW(row.adpopcornRevenue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                            {KRW(row.buzzvilRevenue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                            {KRW(row.rcRevenue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                            {KRW(row.naverDirectRevenue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                            {KRW(row.coupangSellerRevenue)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0044D2' }}
                          >
                            {KRW(total)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
